/* iceberg-ui */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/* MUI */
import { Box, Chip } from '@mui/material';

/* Constants */
import { FontSizes } from 'application/Shared/Constants';

/* React */
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * SaasOpsIsConnectedChip Component
 *
 * This component displays a status chip indicating the connection state of a tenant app's Workato integration.
 *
 * @component
 * @param {Object} props
 * @param {SaasOpsActionItemsState} props.tenantAppState - The state object containing tenant app connection information
 *
 * @returns {JSX.Element} A chip component showing either:
 * - A green check icon with "Connected" text when successfully connected
 * - A warning triangle icon with "Connection Error" text when connection is lost
 */

export const SaasOpsIsConnectedChip = ({
    isConnected,
    lostConnection
}: {
    isConnected: boolean;
    lostConnection: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <>
            {isConnected && (
                <Chip
                    icon={
                        <Box sx={{ lineHeight: 0 }}>
                            <IconWithVariant size={FontSizes[1]} src="Check" />
                        </Box>
                    }
                    sx={{
                        '& .MuiChip-icon': {
                            color: 'saasOpsState.icon.integrated'
                        },
                        '& .MuiChip-label': {
                            paddingX: '8px',
                            paddingY: '2px'
                        },
                        backgroundColor: 'saasOpsState.background.integrated',
                        border: '1px solid',
                        borderColor: 'saasOpsState.borderColor.integrated',
                        color: 'saasOpsState.textColor.integrated'
                    }}
                    size="small"
                    label={t('SaasOps.state.connected')}
                />
            )}
            {lostConnection && (
                <Chip
                    icon={
                        <Box sx={{ lineHeight: 0 }}>
                            <IconWithVariant
                                size={FontSizes[1]}
                                src="WarningTriangleFilled"
                            />
                        </Box>
                    }
                    sx={{
                        '& .MuiChip-icon': {
                            color: 'saasOpsState.icon.error'
                        },
                        '& .MuiChip-label': {
                            paddingX: '8px',
                            paddingY: '2px'
                        },
                        backgroundColor: 'saasOpsState.background.error',
                        border: '1px solid',
                        borderColor: 'saasOpsState.borderColor.error',
                        color: 'saasOpsState.textColor'
                    }}
                    size="small"
                    label={t('SaasOps.connectionError')}
                />
            )}
        </>
    );
};
